import { updateWorkflowTemplate } from '@commenda/api';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import {
  TWorkflowTemplateCategory,
  WORKFLOW_TEMPLATE_CATEGORIES,
} from '@commenda/types';
import { useNotification } from '@commenda/ui';
import { useState } from 'react';
import { WorkflowTemplateMetadataHelper } from './workflow-template-metadata';

const MAX_TAGS = 25;
const MAX_TAG_LENGTH = 64;
const NO_CATEGORY_VALUE = 'NONE';

interface EditWorkflowMetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowTemplateId: string;
  workflowName: string;
  initialTags: string[];
  initialCategory: TWorkflowTemplateCategory | null;
  /** Called after a successful save so the caller can refetch its list. */
  onSaved: () => void;
}

/** Saving replaces both tags and category; the backend merges them into metadata without touching
 * sibling keys. */
export const EditWorkflowMetadataModal = ({
  isOpen,
  onClose,
  workflowTemplateId,
  workflowName,
  initialTags,
  initialCategory,
  onSaved,
}: EditWorkflowMetadataModalProps) => {
  const { setNotification } = useNotification();
  const [tags, setTags] = useState<string[]>(initialTags);
  const [category, setCategory] = useState<
    TWorkflowTemplateCategory | typeof NO_CATEGORY_VALUE
  >(initialCategory ?? NO_CATEGORY_VALUE);
  const [draftTag, setDraftTag] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const onCategoryChange = (value: string) => {
    setCategory(
      WORKFLOW_TEMPLATE_CATEGORIES.find((candidate) => candidate === value) ??
        NO_CATEGORY_VALUE
    );
  };

  const onDraftTagAdd = () => {
    const tag = draftTag.trim().slice(0, MAX_TAG_LENGTH);
    if (!tag) return;
    setTags((previous) =>
      previous.includes(tag) || previous.length >= MAX_TAGS
        ? previous
        : [...previous, tag]
    );
    setDraftTag('');
  };

  const onTagRemove = (tag: string) => {
    setTags((previous) => previous.filter((existing) => existing !== tag));
  };

  const onSave = async () => {
    setIsSaving(true);
    try {
      const response = await updateWorkflowTemplate(workflowTemplateId, {
        tags,
        category: category === NO_CATEGORY_VALUE ? null : category,
      });
      if (!response.ok) {
        console.error(
          'Failed to update workflow metadata',
          await response.text()
        );
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Failed to update tags and category.',
        });
        return;
      }
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Tags and category updated.',
      });
      onSaved();
      onClose();
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update tags and category.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader className="border-b border-b-border-primary">
          <DialogTitle>Edit tags and category</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-xl p-3xl">
          <div className="flex flex-col gap-sm">
            <Label>Category</Label>
            <Select value={category} onValueChange={onCategoryChange}>
              <SelectTrigger>
                <SelectValue placeholder="No category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CATEGORY_VALUE}>No category</SelectItem>
                {WORKFLOW_TEMPLATE_CATEGORIES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {WorkflowTemplateMetadataHelper.CATEGORY_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="txt-small-regular text-text-tertiary">
              The category decides which client request dropdown lists this
              workflow. Without one it appears in none.
            </p>
          </div>
          <p className="txt-small-regular text-text-tertiary">
            Tags for &quot;{workflowName}&quot;. Press Enter to add a tag.
          </p>
          <Input
            placeholder="e.g. US"
            value={draftTag}
            onChange={(e) => setDraftTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onDraftTagAdd();
              }
            }}
          />
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-sm">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => onTagRemove(tag)}
                  aria-label={`Remove ${tag}`}
                >
                  <Badge size="sm">
                    {tag}
                    <Icons.XClose className="size-3" />
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-t-border-primary">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
